import React, { FC, useEffect, useState } from 'react'
import { Col, DatePicker, Row, Select, Spin } from 'antd'
import moment from 'moment'
import { modelApi } from '../../api'
import { RangePickerProps } from 'antd/es/date-picker'
import { formatMomentToApiDate } from '../../utils'
import { IRevenueEnriched } from '../../types/revenue'
import { StyledCollapse } from '../../pages/Results/ResultDetail/styles'
import {
  Area,
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import RChartTooltip from '../Charts/RChartTooltip'
import { useTheme } from 'styled-components'
import { RANGE_TYPES } from '../../constants'

type Props = {
  revenueId: string
}

const RRevenueBarChart: FC<Props> = ({ revenueId }) => {
  const theme = useTheme()
  const [isLoading, setLoading] = useState(true)
  const [revenues, setRevenues] = useState<IRevenueEnriched>([])
  const [rangeType, setRangeType] = useState<string>(RANGE_TYPES.HOUR)
  const [range, setRange] = useState<RangePickerProps['value']>([
    moment('2020-01-01 00:00:00').startOf('month'),
    moment('2020-03-01 00:00:00').endOf('month'),
  ])

  async function fetchData() {
    try {
      setLoading(true)
      const startDate = formatMomentToApiDate(range?.[0] || moment())
      const endDate = formatMomentToApiDate(range?.[1] || moment())
      const revenueData = await modelApi.fetchRevenues({
        id: revenueId,
        startDate,
        endDate,
        range: rangeType,
      })
      const dateTimes = Object.keys(revenueData.revenueAncillary)
      const revenueAncillaries = Object.values(revenueData.revenueAncillary)
      const revenueEnergies = Object.values(revenueData.revenueEnergy)
      const profits = Object.values(revenueData.profit)
      const costs = Object.values(revenueData.cost)

      const enrichedRevenues = Object.values(dateTimes).map((datetime, idx) => ({
        datetime,
        revenueAncillary: revenueAncillaries[idx],
        revenueEnergy: revenueEnergies[idx],
        profit: profits[idx],
        profitAbs: Math.abs(profits[idx]),
        cost: costs[idx],
      })) as IRevenueEnriched

      setRevenues(enrichedRevenues)
      setLoading(false)
    } catch (error) {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [range, rangeType])

  return (
    <Spin spinning={isLoading}>
      <StyledCollapse className='mb-24' defaultActiveKey={1}>
        <StyledCollapse.Panel header='Revenue' key={1}>
          <Row className='mb-8' justify='end' gutter={8}>
            <Col>
              <Select
                value={rangeType}
                options={Object.entries(RANGE_TYPES).map(([key, type]) => ({
                  label: key,
                  value: type,
                }))}
                onChange={setRangeType}
              />
            </Col>
            <Col>
              <DatePicker.RangePicker value={range} onChange={(r) => setRange(r)} />
            </Col>
          </Row>

          <ResponsiveContainer width='100%' height={400}>
            <ComposedChart width={500} height={400} data={revenues}>
              <CartesianGrid strokeDasharray='3 3' stroke='#f5f5f5' />
              <XAxis
                tickFormatter={(date: string) => moment(date).format('DD/MM/YYYY')}
                minTickGap={20}
                dataKey='datetime'
                label={{ value: 'Time', position: 'insideBottomRight', offset: 0 }}
              />
              <YAxis />
              <YAxis
                yAxisId='right'
                orientation='right'
                domain={[0, (dataMax: number) => dataMax * 10]}
                // domain={[0, (dataMax: number) => dataMax * 20]}
                hide
              />
              <Tooltip
                content={<RChartTooltip removeKeys={['profitAbs']} addKeys={['profit']} />}
              />
              <Area
                stackId='plus'
                type='monotone'
                dataKey='revenueAncillary'
                fill={theme.palettes.green[5]}
                stroke={theme.palettes.green[4]}
                strokeWidth={2}
              />
              <Area
                stackId='plus'
                type='monotone'
                dataKey='revenueEnergy'
                fill={theme.palettes.green[6]}
                stroke={theme.palettes.green[7]}
                strokeWidth={2}
              />
              <Area
                type='monotone'
                dataKey='cost'
                strokeDasharray='5'
                fillOpacity={0.1}
                fill={theme.palettes.red[4]}
                stroke={theme.palettes.red[5]}
                strokeWidth={2}
              />
              <Bar dataKey='profitAbs' yAxisId='right'>
                {revenues.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={entry.profit > 0 ? theme.palettes.green[3] : theme.palettes.red[3]}
                    stroke={entry.profit > 0 ? theme.colors.green : theme.colors.red}
                  />
                ))}
              </Bar>
            </ComposedChart>
          </ResponsiveContainer>
        </StyledCollapse.Panel>
      </StyledCollapse>
    </Spin>
  )
}

export default RRevenueBarChart
